import { db } from "../lib/firebase";
import { addDoc, collection, doc, getDoc, getDocs, query, runTransaction, serverTimestamp, updateDoc, where, deleteDoc } from "firebase/firestore";
import { holdInEscrow, increaseBuyerReserved, creditWallet } from "./wallet";

// Campus Market keeps this share of every ticket sale; the seller gets the rest.
export const TICKET_PLATFORM_FEE_RATE = 0.05;

export interface TicketTier {
  name: string;
  price: number;
  quantity?: number;   // optional cap for this tier
  soldCount?: number;
  perks?: string;
  codePrefix?: string; // optional prefix for ticket codes, e.g. "R", "V", "VIP"
}

export interface TicketLink {
  id?: string;
  sellerId: string;
  sellerName?: string;
  sellerEmail?: string;
  title: string;
  description?: string;
  price: number;          // base/lowest price (fallback when no tiers)
  tiers?: TicketTier[];
  eventDate?: string;
  venue?: string;
  state?: string;
  university?: string;
  onlineUrl?: string;     // for online events (Zoom, Meet, YouTube, etc.)
  images?: string[];
  listingId?: string;
  visibility?: 'public' | 'private'; // public shows on marketplace; private is link-only
  maxQuantity?: number; // optional overall cap
  soldCount?: number;
  status?: "active" | "inactive";
  createdAt?: any;
}

export interface TicketPurchaseResult {
  orderId: string;
  code: string; // unique ticket code assigned to buyer
}

// Create a ticket link for a seller/marketer to share
export async function createTicketLink(data: {
  sellerId: string;
  sellerName?: string;
  sellerEmail?: string;
  title: string;
  price: number;
  description?: string;
  maxQuantity?: number;
  tiers?: TicketTier[];
  eventDate?: string;
  venue?: string;
  state?: string;
  university?: string;
  onlineUrl?: string;
  images?: string[];
  listingId?: string;
  visibility?: 'public' | 'private';
}): Promise<{ id: string }>{
  // Strip undefined (Firestore rejects it).
  const payload: any = {
    sellerId: data.sellerId,
    title: data.title,
    price: data.price,
    soldCount: 0,
    status: "active",
    createdAt: serverTimestamp(),
  };
  if (data.sellerName) payload.sellerName = data.sellerName;
  if (data.sellerEmail) payload.sellerEmail = data.sellerEmail;
  if (data.description) payload.description = data.description;
  if (typeof data.maxQuantity === 'number') payload.maxQuantity = data.maxQuantity;
  if (data.tiers && data.tiers.length) {
    // Firestore rejects undefined — build each tier with only defined fields.
    payload.tiers = data.tiers.map(t => {
      const tier: any = { name: t.name, price: Number(t.price) || 0, soldCount: t.soldCount ?? 0 };
      if (typeof t.quantity === 'number' && !isNaN(t.quantity)) tier.quantity = t.quantity;
      if (t.perks) tier.perks = t.perks;
      if (t.codePrefix && t.codePrefix.trim()) tier.codePrefix = t.codePrefix.trim();
      return tier;
    });
  }
  if (data.eventDate) payload.eventDate = data.eventDate;
  if (data.venue) payload.venue = data.venue;
  if (data.state) payload.state = data.state;
  if (data.university) payload.university = data.university;
  if (data.onlineUrl) payload.onlineUrl = data.onlineUrl;
  payload.visibility = data.visibility === 'private' ? 'private' : 'public';
  if (data.images && data.images.length) payload.images = data.images;
  if (data.listingId) payload.listingId = data.listingId;

  const added = await addDoc(collection(db, "ticket_links"), payload);
  return { id: added.id };
}

export async function getTicketLink(linkId: string): Promise<TicketLink | null> {
  const snap = await getDoc(doc(db, "ticket_links", linkId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...(snap.data() as TicketLink) };
}

// Finds the ticket link for a listing: by stored listingId first, then (for older
// tickets) by matching the same seller + title.
export async function getTicketLinkByListingId(
  listingId: string,
  opts?: { sellerId?: string; title?: string }
): Promise<TicketLink | null> {
  try {
    const byListing = await getDocs(query(collection(db, "ticket_links"), where("listingId", "==", listingId)));
    if (!byListing.empty) {
      const d = byListing.docs[0];
      return { id: d.id, ...(d.data() as TicketLink) };
    }
    if (opts?.sellerId && opts?.title) {
      const bySeller = await getDocs(query(collection(db, "ticket_links"), where("sellerId", "==", opts.sellerId)));
      const match = bySeller.docs.find((d) => String((d.data() as any).title || '').trim().toLowerCase() === opts.title!.trim().toLowerCase());
      if (match) return { id: match.id, ...(match.data() as TicketLink) };
    }
    return null;
  } catch {
    return null;
  }
}

export async function listSellerTicketLinks(sellerId: string): Promise<TicketLink[]> {
  const q = query(collection(db, "ticket_links"), where("sellerId", "==", sellerId));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...(d.data() as TicketLink) }));
}

// Deletes a ticket event: removes the ticket_link and its marketplace listing.
export async function deleteTicketLink(linkId: string): Promise<void> {
  const snap = await getDoc(doc(db, "ticket_links", linkId));
  const listingId = snap.exists() ? (snap.data() as any)?.listingId : null;
  await deleteDoc(doc(db, "ticket_links", linkId));
  if (listingId) {
    try { await deleteDoc(doc(db, "listings", listingId)); } catch { /* listing may already be gone */ }
  }
}

export async function updateTicketLink(linkId: string, updates: Partial<TicketLink>) {
  await updateDoc(doc(db, "ticket_links", linkId), updates as any);
}

// Purchase a ticket via a link. Checks wallet balance and assigns unique code.
export async function purchaseTicketViaLink(params: { buyerId: string; linkId: string }): Promise<TicketPurchaseResult> {
  const { buyerId, linkId } = params;

  // 1) Load link and basic validations
  const link = await getTicketLink(linkId);
  if (!link) throw new Error("Ticket link not found");
  if (link.status === "inactive") throw new Error("Ticket link is inactive");
  if (typeof link.maxQuantity === "number" && typeof link.soldCount === "number" && link.soldCount >= link.maxQuantity) {
    throw new Error("Tickets are sold out");
  }

  // Idempotency lock to prevent duplicate purchases by same buyer for same link
  const lockRef = doc(db, 'ticket_purchase_locks', `${buyerId}-${linkId}`);
  await runTransaction(db, async (tx) => {
    const lockSnap = await tx.get(lockRef);
    if (lockSnap.exists()) {
      throw new Error('A purchase is already in progress. Please wait.');
    }
    tx.set(lockRef, { createdAt: serverTimestamp() });
  });

  try {
    // If a purchase already exists for this buyer+link, return existing result
    const existingQ = query(collection(db, 'ticket_purchases'), where('buyerId', '==', buyerId), where('linkId', '==', linkId));
    const existingSnap = await getDocs(existingQ);
    if (!existingSnap.empty) {
      const first = existingSnap.docs[0];
      const data = first.data() as any;
      // Cleanup lock before returning
      try { await deleteDoc(lockRef); } catch {}
      return { orderId: String(data.orderId || ''), code: String(data.code || '') };
    }

    // 2) Ensure buyer has enough wallet balance and debit into reserved
    const walletRef = doc(db, "wallets", buyerId);
    await runTransaction(db, async (tx) => {
      const snap = await tx.get(walletRef);
      const current = snap.exists() ? (snap.data() as any) : { balance: 0, escrowBalance: 0, totalEarnings: 0, reservedBalance: 0 };
      const balance = Number(current.balance || 0);
      if (balance < link.price) {
        throw new Error("Insufficient wallet balance");
      }
      const nextBalance = balance - link.price;
      tx.set(walletRef, { ...current, balance: nextBalance }, { merge: true });
    });
    // Track reserved for buyer
    await increaseBuyerReserved(buyerId, link.price);

    // Client-side wallet transaction entry for visibility
    try {
      await addDoc(collection(walletRef, "transactions"), {
        type: "debit",
        amount: link.price,
        description: `Ticket purchase via link ${linkId}`,
        status: "held",
        method: "internal",
        reference: `TL-${linkId}-${Date.now()}`,
        createdAt: serverTimestamp(),
      } as any);
    } catch {}

    // 3) Create order for the seller and hold amount in escrow
    const orderDoc = {
      buyerId,
      sellerId: link.sellerId,
      status: "payment_held" as const,
      totalAmount: link.price,
      orderItems: [
        { title: link.title, price: link.price, quantity: 1 }
      ],
      createdAt: serverTimestamp(),
      paymentReference: `TL-${linkId}-${Date.now()}`,
    } as any;
    const addedOrder = await addDoc(collection(db, "orders"), orderDoc);
    await holdInEscrow(link.sellerId, link.price, addedOrder.id, orderDoc.paymentReference);

    // 4) Assign unique ticket code and persist purchase record (include sellerId for easy queries)
    const code = generateTicketCode(linkId);
    await addDoc(collection(db, "ticket_purchases"), {
      linkId,
      sellerId: link.sellerId,
      buyerId,
      orderId: addedOrder.id,
      code,
      createdAt: serverTimestamp(),
    });

    // 5) Increment soldCount on the link for tracking
    try {
      const linkRef = doc(db, "ticket_links", linkId);
      await runTransaction(db, async (t) => {
        const s = await t.get(linkRef);
        const d = s.exists() ? (s.data() as any) : {};
        const nextSold = Number(d.soldCount || 0) + 1;
        t.set(linkRef, { ...d, soldCount: nextSold }, { merge: true });
      });
    } catch {}

    // Cleanup lock
    try { await deleteDoc(lockRef); } catch {}

    return { orderId: addedOrder.id, code };
  } catch (e) {
    // Cleanup lock on error
    try { await deleteDoc(lockRef); } catch {}
    throw e;
  }
}

// Records a GUEST (no-account) ticket purchase after Paystack payment is verified.
// Platform keeps TICKET_PLATFORM_FEE_RATE; the seller's share is credited to their
// Campus Market wallet (they withdraw it via the normal payout). Returns ticket codes.
export async function recordGuestTicketPurchase(params: {
  linkId: string;
  tierName?: string;
  unitPrice: number;
  quantity: number;
  buyerName: string;
  buyerEmail: string;
  buyerPhone?: string;
  paymentReference: string;
}): Promise<{ codes: string[]; purchaseId: string; total: number; sellerAmount: number; platformFee: number }> {
  const link = await getTicketLink(params.linkId);
  if (!link) throw new Error("Ticket link not found");

  const qty = Math.max(1, Math.floor(params.quantity));
  // Resolve a safe unit price: trust the client value, else fall back to the matching
  // tier's price (or base price) so a bad/NaN value can never crash the record.
  const linkTier = (link.tiers || []).find(
    (t) => (t.name || '').trim().toLowerCase() === (params.tierName || '').trim().toLowerCase()
  );
  let unit = Number(params.unitPrice);
  if (!isFinite(unit) || unit <= 0) unit = Number(linkTier?.price) || Number(link.price) || 0;
  const total = Math.round(unit * qty) || 0;
  const platformFee = Math.round(total * TICKET_PLATFORM_FEE_RATE) || 0;
  const sellerAmount = total - platformFee;

  // Deterministic doc id keyed on the payment reference makes recording idempotent and
  // race-safe: the browser AND the Paystack webhook can both call this, but the atomic
  // create-if-absent below ensures the seller is credited exactly once.
  const refId = String(params.paymentReference).replace(/[\/\s#?]/g, '_');
  const purchaseRef = doc(db, 'ticket_purchases', refId);
  const walletRef = doc(db, 'wallets', link.sellerId);

  let codes: string[] = [];
  let created = false;

  await runTransaction(db, async (t) => {
    const existing = await t.get(purchaseRef);
    if (existing.exists()) {
      const d = existing.data() as any;
      codes = Array.isArray(d.codes) ? d.codes : [d.code].filter(Boolean);
      return; // already recorded — never credit twice
    }
    const wSnap = await t.get(walletRef);
    const w = wSnap.exists() ? (wSnap.data() as any) : { balance: 0, escrowBalance: 0, totalEarnings: 0 };
    codes = Array.from({ length: qty }, () => generateTicketCode(params.linkId, linkTier?.codePrefix));
    t.set(purchaseRef, {
      linkId: params.linkId, listingId: link.listingId || null, sellerId: link.sellerId,
      buyerId: null, guest: true, buyerName: params.buyerName, buyerEmail: params.buyerEmail,
      buyerPhone: params.buyerPhone || null, tierName: params.tierName || null,
      unitPrice: unit, quantity: qty, totalAmount: total, platformFee, sellerAmount,
      codes, code: codes[0], paymentReference: params.paymentReference, status: 'paid',
      createdAt: serverTimestamp(),
    });
    t.set(walletRef, { ...w, balance: (w.balance || 0) + sellerAmount }, { merge: true });
    created = true;
  });

  if (created) {
    // Non-critical follow-ups (own transactions) — safe to skip on failure.
    try {
      await addDoc(collection(walletRef, 'transactions'), {
        type: 'credit', amount: sellerAmount,
        description: `Ticket sale: ${link.title}${params.tierName ? ` (${params.tierName})` : ''} ×${qty}`,
        status: 'completed', orderId: refId, createdAt: serverTimestamp(),
      });
    } catch {}
    try {
      const linkRef = doc(db, 'ticket_links', params.linkId);
      await runTransaction(db, async (t) => {
        const s = await t.get(linkRef);
        const d = s.exists() ? (s.data() as any) : {};
        const tiers = Array.isArray(d.tiers) ? d.tiers.map((x: any) => ({ ...x })) : [];
        if (params.tierName && tiers.length) {
          const want = String(params.tierName).trim().toLowerCase();
          const idx = tiers.findIndex((x: any) => String(x.name || '').trim().toLowerCase() === want);
          if (idx >= 0) tiers[idx].soldCount = Number(tiers[idx].soldCount || 0) + qty;
        }
        t.set(linkRef, { soldCount: Number(d.soldCount || 0) + qty, tiers }, { merge: true });
      });
    } catch {}
  }

  return { codes, purchaseId: refId, total, sellerAmount, platformFee };
}

export async function listTicketPurchasesBySeller(sellerId: string): Promise<any[]> {
  const q = query(collection(db, "ticket_purchases"), where("sellerId", "==", sellerId));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...(d.data() as any) }));
}

function generateTicketCode(linkId: string, prefix?: string): string {
  const rand = Math.floor(1000 + Math.random() * 9000);
  const base = `${linkId.slice(0, 6).toUpperCase()}-${Date.now().toString().slice(-5)}-${rand}`;
  // Seller-chosen prefix (e.g. R / V / VIP) when set, else the default TL.
  const p = (prefix || 'TL').replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 6) || 'TL';
  return `${p}-${base}`;
}